import { forwardRef, useImperativeHandle, useState } from 'react';
import {
  adminClientsService,
  type ClientDetail,
  type ClientExtractionPolicyPatch,
} from '../../../services/adminClientsService';
import { toastSuccess, toastError } from '../../../lib/toast';
import type { AutonomousTabHandle } from './IdentityTab';
import { SystemTenantNotice } from './SystemTenantNotice';

interface Props {
  clientId: string;
  initialData: ClientDetail;
  onSaved?: (updated: ClientDetail) => void;
}

interface LocalState {
  extraction_policy: 'auto' | 'manual' | 'scheduled';
  extraction_schedule_cron: string | null;
}

const toLocal = (c: ClientDetail): LocalState => ({
  extraction_policy: c.extraction_policy as 'auto' | 'manual' | 'scheduled',
  extraction_schedule_cron: c.extraction_schedule_cron,
});

const describeCron = (cron: string): string => {
  const c = cron.trim();
  if (c === '0 * * * *') return 'Toutes les heures à la minute 0';
  if (c === '*/5 * * * *') return 'Toutes les 5 minutes';
  if (c === '0 0 * * *') return 'Tous les jours à minuit';
  const m = c.match(/^0 \*\/(\d+) \* \* \*$/);
  if (m) return `Toutes les ${m[1]} heure(s) à la minute 0`;
  return '';
};

export const ExtractionTab = forwardRef<AutonomousTabHandle, Props>(
  ({ clientId, initialData, onSaved }, ref) => {
    const [state, setState] = useState<LocalState>(() => toLocal(initialData));
    const [baseline, setBaseline] = useState<LocalState>(() => toLocal(initialData));
    const [saving, setSaving] = useState(false);

    const isDirty = JSON.stringify(state) !== JSON.stringify(baseline);

    useImperativeHandle(ref, () => ({
      getIsDirty: () => isDirty,
      reset: () => setState(baseline),
    }));

    const handleSave = async () => {
      if (!isDirty) return;
      setSaving(true);
      try {
        const payload: ClientExtractionPolicyPatch = {};
        if (state.extraction_policy !== baseline.extraction_policy)
          payload.extraction_policy = state.extraction_policy;
        if (state.extraction_schedule_cron !== baseline.extraction_schedule_cron)
          payload.extraction_schedule_cron = state.extraction_schedule_cron;
        const updated = await adminClientsService.patchExtractionPolicy(clientId, payload);
        setBaseline(toLocal(updated));
        setState(toLocal(updated));
        toastSuccess('Extraction enregistrée');
        onSaved?.(updated);
      } catch (err) {
        toastError('Enregistrement échoué', err);
      } finally {
        setSaving(false);
      }
    };

    const isScheduled = state.extraction_policy === 'scheduled';
    const cron = state.extraction_schedule_cron || '';
    const human = describeCron(cron);
    const isSystem = initialData.is_system_tenant === true;

    return (
      <div className="space-y-4 max-w-2xl">
        {isSystem && <SystemTenantNotice what="aucune extraction ne tourne sur ce tenant" />}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Politique d'extraction</label>
          <select
            value={state.extraction_policy}
            disabled={isSystem}
            onChange={(e) =>
              setState((s) => ({
                ...s,
                extraction_policy: e.target.value as 'auto' | 'manual' | 'scheduled',
              }))
            }
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm disabled:bg-gray-100"
          >
            <option value="auto">Automatique (à l'upload)</option>
            <option value="manual">Manuelle (clic UI)</option>
            <option value="scheduled">Planifiée (cron)</option>
          </select>
        </div>

        {isScheduled && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expression cron
            </label>
            <input
              type="text"
              value={cron}
              disabled={isSystem}
              onChange={(e) =>
                setState((s) => ({ ...s, extraction_schedule_cron: e.target.value || null }))
              }
              placeholder="0 * * * *"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono disabled:bg-gray-100"
            />
            <p className="text-xs text-gray-500 mt-1">
              Format standard 5 champs : minute heure jour mois jour-semaine.
            </p>
            {human && <p className="text-xs text-blue-700 mt-1">{human}</p>}
          </div>
        )}

        <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-white border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || saving || isSystem}
            className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    );
  },
);

ExtractionTab.displayName = 'ExtractionTab';

export default ExtractionTab;
